import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Platforms available in the Paylaş modal. Order drives tab order (FR-006).
 * String values match the `utm_source` enum recognised by the backend
 * UtmVisitsController (case-insensitive).
 */
type SharePlatform = 'whatsapp' | 'facebook' | 'instagram' | 'google';

interface PlatformDescriptor {
  id: SharePlatform;
  label: string;
  icon: string;
  /**
   * Returns the target URL for the platform's primary "share" action, or
   * `null` when the primary action is copy-to-clipboard (Instagram).
   */
  buildShareUrl: (url: string, title: string) => string | null;
}

const PLATFORMS: readonly PlatformDescriptor[] = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'fab fa-whatsapp',
    buildShareUrl: (url) => `https://wa.me/?text=${encodeURIComponent(url)}`,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'fab fa-facebook',
    buildShareUrl: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: 'fab fa-instagram',
    buildShareUrl: () => null,
  },
  {
    id: 'google',
    label: 'Google',
    icon: 'fab fa-google',
    buildShareUrl: (url, title) =>
      `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
        title,
      )}&body=${encodeURIComponent(url)}`,
  },
] as const;

interface ShareListingModalProps {
  show: boolean;
  onClose: () => void;
  listingId: string;
  listingTitle?: string;
  /** Button that opened the modal — focus is restored on close for accessibility. */
  triggerRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * Writes a string to the clipboard with a legacy `execCommand('copy')` fallback
 * for browsers/insecure contexts where `navigator.clipboard` is unavailable.
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy fallback
    }
  }
  try {
    const input = document.createElement('input');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(input);
    return ok;
  } catch {
    return false;
  }
};

/**
 * Bootstrap 5 modal that presents four share targets for a listing; each tab
 * shows the canonical listing URL tagged with a platform-specific
 * `utm_source` query parameter.
 *
 * Implements feature spec FR-006..FR-009. Uses only existing Bootstrap classes
 * and FontAwesome icons — no new dependency.
 */
const ShareListingModal: React.FC<ShareListingModalProps> = ({
  show,
  onClose,
  listingId,
  listingTitle,
  triggerRef,
}) => {
  const [activePlatform, setActivePlatform] = useState<SharePlatform>('whatsapp');
  const [toast, setToast] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const urlsBySource = useMemo(() => {
    if (typeof window === 'undefined') {
      return PLATFORMS.reduce<Record<SharePlatform, string>>(
        (acc, platform) => {
          acc[platform.id] = '';
          return acc;
        },
        {} as Record<SharePlatform, string>,
      );
    }
    // Preserve path + any existing query; merge UTM (spec 012: non-destructive).
    const base = new URL(window.location.href);
    return PLATFORMS.reduce<Record<SharePlatform, string>>(
      (acc, platform) => {
        const u = new URL(base);
        u.searchParams.set('utm_source', platform.id);
        u.searchParams.set('utm_medium', 'share');
        acc[platform.id] = u.toString();
        return acc;
      },
      {} as Record<SharePlatform, string>,
    );
  }, [listingId, show]);

  useEffect(() => {
    if (!show) return;
    setActivePlatform('whatsapp');
    setToast(null);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, onClose]);

  useEffect(() => {
    if (show) {
      const firstTab = dialogRef.current?.querySelector<HTMLButtonElement>(
        'button.nav-link.active',
      );
      firstTab?.focus();
    } else if (triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [show, triggerRef]);

  const currentUrl = urlsBySource[activePlatform];
  const currentPlatform = PLATFORMS.find((p) => p.id === activePlatform)!;

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(currentUrl);
    setToast(ok ? 'Bağlantı kopyalandı' : 'Kopyalama başarısız');
  }, [currentUrl]);

  const handlePrimaryAction = useCallback(async () => {
    const shareUrl = currentPlatform.buildShareUrl(
      currentUrl,
      listingTitle ?? 'SadeceBilgisayar.com ilanı',
    );
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const ok = await copyToClipboard(currentUrl);
    setToast(
      ok
        ? "Bağlantı kopyalandı — Instagram'da yapıştırabilirsiniz."
        : 'Kopyalama başarısız',
    );
  }, [currentPlatform, currentUrl, listingTitle]);

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shareListingModalTitle"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          ref={dialogRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="shareListingModalTitle">
                <i className="fas fa-share-alt me-2"></i>İlanı Paylaş
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Kapat"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <ul className="nav nav-tabs mb-3" role="tablist">
                {PLATFORMS.map((p) => (
                  <li key={p.id} className="nav-item" role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activePlatform === p.id}
                      className={`nav-link ${activePlatform === p.id ? 'active' : ''}`}
                      onClick={() => setActivePlatform(p.id)}
                    >
                      <i className={`${p.icon} me-2`} />
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mb-3">
                <label htmlFor="shareListingUrl" className="form-label">
                  Paylaşım bağlantısı
                </label>
                <div className="input-group">
                  <input
                    id="shareListingUrl"
                    type="text"
                    className="form-control"
                    readOnly
                    value={currentUrl}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCopy}
                  >
                    <i className="fas fa-copy me-1" /> Kopyala
                  </button>
                </div>
              </div>
              {toast && (
                <div className="alert alert-info py-2 mb-3" role="status">
                  {toast}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                Kapat
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePrimaryAction}
              >
                <i className={`${currentPlatform.icon} me-2`} />
                {activePlatform === 'instagram' ? 'Bağlantıyı Kopyala' : `${currentPlatform.label}'da Paylaş`}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default ShareListingModal;
